/* =====================================================================
   SUTRAVAPA — SHARED BASE BEHAVIOUR
   Load this ONCE, theme-wide, right before </body> in layout/theme.liquid:
     <script src="{{ 'sutravapa-base.js' | asset_url }}" defer></script>
   Every behaviour below is guarded (checks the element exists before wiring
   up), so it is safe to include this file even on pages/templates that
   don't use every Sutravapa section.
   ===================================================================== */
(function(){
  "use strict";
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Always land at the top of the page ----------
     Browsers auto-restore scroll position on back/forward, which can leave
     a page mid-scroll instead of at top. We force top-of-page on every
     navigation (including back/forward via "pageshow"), except when the
     URL has a #hash — that means a link is intentionally pointing at an
     on-page anchor (e.g. "View Matching Sarees" -> #weaves) and should be
     left alone. This never touches which page the back button lands on —
     only where that page is scrolled to once it's there. */
  if('scrollRestoration' in history){ history.scrollRestoration = 'manual'; }
  var scrollToTopIfNoHash = function(){
    if(!window.location.hash){ window.scrollTo(0, 0); }
  };
  scrollToTopIfNoHash();
  /* Only reset scroll for a genuine back/forward bfcache restore
     (event.persisted === true) — NOT every "pageshow" event. Some mobile
     browsers fire pageshow in other situations too (tab restore, app
     foregrounding), and reacting to all of them was yanking the page back
     to the very top mid-scroll on the live site — looking like the page
     snapping/shaking on its own with no obvious trigger. */
  window.addEventListener('pageshow', function(e){
    if(e.persisted) scrollToTopIfNoHash();
  });

  /* ---------- Announcement bar: rotate through messages ---------- */
  var announceEl = document.getElementById('announce-text');
  if(announceEl){
    var messages = [];
    try{ messages = JSON.parse(announceEl.getAttribute('data-messages') || '[]'); }catch(e){}
    if(messages.length > 1){
      var mi = 0;
      setInterval(function(){
        mi = (mi + 1) % messages.length;
        announceEl.style.opacity = 0;
        setTimeout(function(){
          announceEl.textContent = messages[mi];
          announceEl.style.opacity = 1;
          /* Re-measure --announce-h here too: if this message wraps to a
             different number of lines than the last one, the bar's real
             height just changed. Without this, the sticky header (bound to
             --announce-h) silently drifts out of sync every rotation,
             forever — independent of scrolling, which looked like constant
             background shaking with no obvious trigger. */
          measureAnnounce();
        }, 250);
      }, 4200);
    }
  }

  /* ---------- Bridge the announcement bar's height to the header's sticky
     offset (--announce-h), since they're independent sections that can't
     share one sticky parent. Re-measured on load/resize since the bar's
     height can change with font size, safe-area insets, etc. ---------- */
  var announceBar = document.getElementById('announce-bar');
  function measureAnnounce(){
    if(!announceBar || announceBar.classList.contains('hidden')) return;
    document.documentElement.style.setProperty('--announce-h', announceBar.offsetHeight + 'px');
  }
  measureAnnounce();
  window.addEventListener('resize', measureAnnounce);
  window.addEventListener('load', measureAnnounce);

  /* ---------- Mobile nav drawer ---------- */
  var mobileNav = document.getElementById('mobile-nav');
  if(mobileNav){
    var openBtn = document.getElementById('nav-open');
    var closeNavBtn = document.getElementById('nav-close');
    var backdrop = document.getElementById('nav-backdrop');
    function openNav(){ mobileNav.classList.add('open'); }
    function closeNav(){ mobileNav.classList.remove('open'); }
    if(openBtn) openBtn.addEventListener('click', openNav);
    if(closeNavBtn) closeNavBtn.addEventListener('click', closeNav);
    if(backdrop) backdrop.addEventListener('click', closeNav);
    document.addEventListener('keydown', function(e){ if(e.key === 'Escape') closeNav(); });
    mobileNav.querySelectorAll('a').forEach(function(a){ a.addEventListener('click', closeNav); });
  }

  /* ---------- Product page: variant picker + gallery ----------
     Wrapped in try/catch so a bug in this one feature can never halt the
     rest of the script — without this, an uncaught error here would stop
     every later block (reveal animations, wishlist, video lightbox, etc.)
     from initializing on the product page, since it's all one synchronous
     script execution. ---------- */
  document.querySelectorAll('.product-form').forEach(function(form){
   try{
    var idInput = form.querySelector('input[name="id"]');
    if(!idInput) return;
    var sectionId = idInput.id.replace('ProductVariantId-', '');
    var variantsScript = document.getElementById('ProductVariants-' + sectionId);
    var optionSelects = form.querySelectorAll('[data-option-index]');
    var priceWrap = document.getElementById('ProductPrice-' + sectionId);
    var addBtn = document.getElementById('ProductAddToCart-' + sectionId);
    var moneyFormat = function(cents){ return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

    /* Base price/compare-at (excluding any add-on) — kept as mutable state
       so both variant switching AND the add-on checkbox can independently
       trigger a re-render of the same price display without stomping on
       each other's changes. */
    var basePrice = priceWrap ? parseInt(priceWrap.getAttribute('data-base-price'), 10) || 0 : 0;
    var baseComparePrice = priceWrap ? parseInt(priceWrap.getAttribute('data-base-compare-price'), 10) || 0 : 0;
    var addonCheckboxEl = document.getElementById('ProductAddon-' + sectionId);
    var renderPrice = function(){
      if(!priceWrap) return;
      var addonAmount = (addonCheckboxEl && addonCheckboxEl.checked) ? (parseInt(addonCheckboxEl.getAttribute('data-addon-price'), 10) || 0) : 0;
      var displayPrice = basePrice + addonAmount;
      var priceHtml = '';
      if(baseComparePrice && baseComparePrice > basePrice){
        var pct = Math.round((baseComparePrice - basePrice) / baseComparePrice * 100);
        priceHtml += '<span class="mrp">' + moneyFormat(baseComparePrice) + '</span>';
        priceHtml += '<span class="price">' + moneyFormat(displayPrice) + '</span>';
        priceHtml += '<span class="pdp-save-badge">(' + pct + '% Off)</span>';
      } else {
        priceHtml += '<span class="price">' + moneyFormat(displayPrice) + '</span>';
      }
      priceWrap.innerHTML = priceHtml;
    };
    if(addonCheckboxEl) addonCheckboxEl.addEventListener('change', renderPrice);

    if(variantsScript && optionSelects.length){
      var variants = [];
      try{ variants = JSON.parse(variantsScript.textContent); }catch(e){}
      var updateVariant = function(){
        var selected = Array.prototype.map.call(optionSelects, function(s){ return s.value; });
        var match = variants.find(function(v){
          return selected.every(function(val, i){ return v.options[i] === val; });
        });
        if(!match) return;
        idInput.value = match.id;
        basePrice = match.price;
        baseComparePrice = match.compare_at_price || 0;
        renderPrice();
        if(addBtn){
          addBtn.disabled = !match.available;
          addBtn.textContent = match.available ? addBtn.getAttribute('data-available-label') || addBtn.textContent : addBtn.getAttribute('data-soldout-label') || 'Sold Out';
        }
      };
      optionSelects.forEach(function(s){ s.addEventListener('change', updateVariant); });
    }

    var gallery = document.querySelector('.product-gallery');
    if(gallery){
      var thumbs = gallery.querySelectorAll('.product-gallery-thumb');
      var slides = gallery.querySelectorAll('.product-gallery-slide');
      thumbs.forEach(function(thumb){
        thumb.addEventListener('click', function(){
          var id = thumb.getAttribute('data-media-id');
          thumbs.forEach(function(t){ t.classList.toggle('active', t === thumb); });
          slides.forEach(function(s){ s.classList.toggle('active', s.getAttribute('data-media-id') === id); });
        });
      });
    }

    /* Quantity stepper +/- buttons */
    var qtyInput = form.querySelector('#ProductQty-' + sectionId);
    var qtyDecrease = form.querySelector('[data-qty-decrease]');
    var qtyIncrease = form.querySelector('[data-qty-increase]');
    if(qtyInput && qtyDecrease && qtyIncrease){
      qtyDecrease.addEventListener('click', function(){ qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); });
      qtyIncrease.addEventListener('click', function(){ qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; });
    }

    /* Fall & Pico Bidding (or any similar) add-on: when checked, its
       product is added to the cart as a second real line item alongside
       the main product, in the same request — so its price genuinely adds
       to the order total and shows at checkout, with no app required. */
    var addonCheckbox = document.getElementById('ProductAddon-' + sectionId);
    var buildCartItems = function(){
      var items = [{ id: parseInt(idInput.value, 10), quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1 }];
      if(addonCheckbox && addonCheckbox.checked){
        var addonId = parseInt(addonCheckbox.getAttribute('data-addon-variant-id'), 10);
        if(addonId) items.push({ id: addonId, quantity: 1 });
      }
      return items;
    };

    var feedbackEl = document.getElementById('ProductAddFeedback-' + sectionId);
    var updateCartBadge = function(cart){
      document.querySelectorAll('[data-cart-count-wrap]').forEach(function(wrap){
        var badge = wrap.querySelector('.cart-count');
        if(cart.item_count > 0 && !badge){
          badge = document.createElement('span'); badge.className = 'cart-count'; wrap.appendChild(badge);
        }
        if(badge){ badge.textContent = cart.item_count; badge.style.display = cart.item_count > 0 ? '' : 'none'; }
      });
    };
    var addToCart = function(items){
      return fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function(res){ return res.json().then(function(data){ return { ok: res.ok, data: data }; }); })
        .then(function(result){
          if(!result.ok){ throw new Error((result.data && result.data.description) || 'Could not add this to your cart.'); }
          return fetch('/cart.js').then(function(r){ return r.json(); });
        });
    };

    /* Real AJAX add-to-cart: posts to Shopify's cart API, updates the
       header's cart badge live, and shows an inline success/error message —
       no full-page reload, and falls back to a normal form submit if fetch
       isn't available. */
    if(window.fetch){
      form.addEventListener('submit', function(e){
        e.preventDefault();
        var originalLabel = addBtn ? addBtn.textContent : '';
        if(addBtn){ addBtn.disabled = true; addBtn.textContent = 'Adding…'; }
        if(feedbackEl){ feedbackEl.textContent = ''; feedbackEl.className = 'product-add-feedback'; }
        addToCart(buildCartItems())
          .then(function(cart){
            if(feedbackEl){ feedbackEl.textContent = 'Added to cart!'; feedbackEl.classList.add('success'); }
            if(cart) updateCartBadge(cart);
          })
          .catch(function(err){
            if(feedbackEl){ feedbackEl.textContent = err.message || 'Something went wrong — please try again.'; feedbackEl.classList.add('error'); }
          })
          .finally(function(){
            if(addBtn){ addBtn.disabled = false; addBtn.textContent = originalLabel; }
          });
      });

      var buyNowBtn = document.getElementById('ProductBuyNow-' + sectionId);
      if(buyNowBtn){
        buyNowBtn.addEventListener('click', function(){
          var originalLabel = buyNowBtn.textContent;
          buyNowBtn.disabled = true; buyNowBtn.textContent = 'Please wait…';
          if(feedbackEl){ feedbackEl.textContent = ''; feedbackEl.className = 'product-add-feedback'; }
          addToCart(buildCartItems())
            .then(function(){ window.location.href = '/checkout'; })
            .catch(function(err){
              if(feedbackEl){ feedbackEl.textContent = err.message || 'Something went wrong — please try again.'; feedbackEl.classList.add('error'); }
              buyNowBtn.disabled = false; buyNowBtn.textContent = originalLabel;
            });
        });
      }
    }
   }catch(err){
     if(window.console && console.error) console.error('Sutravapa: product-form init failed for one form, rest of the page continues normally.', err);
   }
  });

  /* ---------- Wishlist (localStorage-based — Shopify has no native
     wishlist, so this saves product handles in the browser and re-hydrates
     real product data via Shopify's public /products/{handle}.js endpoint
     wherever it's needed). Works across every page via event delegation,
     since heart buttons can be added/removed from the DOM at any time. ---------- */
  var WISHLIST_KEY = 'sutravapa_wishlist';
  function getWishlist(){
    try{ return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); }catch(e){ return []; }
  }
  function setWishlist(list){
    try{ localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }catch(e){}
    updateWishlistUI();
  }
  function updateWishlistUI(){
    var list = getWishlist();
    document.querySelectorAll('.wishlist-count').forEach(function(el){
      el.textContent = list.length;
      el.style.display = list.length > 0 ? '' : 'none';
    });
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function(btn){
      btn.classList.toggle('active', list.indexOf(btn.getAttribute('data-wishlist-toggle')) > -1);
    });
  }
  document.addEventListener('click', function(e){
    var btn = e.target.closest('[data-wishlist-toggle]');
    if(!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-wishlist-toggle');
    var list = getWishlist();
    var idx = list.indexOf(handle);
    if(idx > -1){ list.splice(idx, 1); } else { list.push(handle); }
    setWishlist(list);
  });
  updateWishlistUI();
  window.addEventListener('pageshow', updateWishlistUI);

  /* ---------- Related products: fetched via Shopify's real recommendations
     endpoint (the `recommendations` Liquid object only populates when a
     section is re-rendered through this URL, not on a normal page load). ---------- */
  var relatedWrap = document.querySelector('.related-products-content');
  if(relatedWrap && window.fetch){
    var recUrl = relatedWrap.getAttribute('data-recommendations-url');
    if(recUrl){
      fetch(recUrl)
        .then(function(r){ return r.ok ? r.text() : ''; })
        .then(function(html){
          if(!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById(relatedWrap.id);
          if(fresh && fresh.innerHTML.trim()){
            relatedWrap.innerHTML = fresh.innerHTML;
          } else {
            var section = relatedWrap.closest('[data-section-name="Related Products"]');
            if(section) section.style.display = 'none';
          }
        })
        .catch(function(){});
    }
  }

  /* ---------- Recently viewed products (localStorage) ---------- */
  var RECENTLY_VIEWED_KEY = 'sutravapa_recently_viewed';
  var currentProductHandle = document.body.getAttribute('data-product-handle');
  if(currentProductHandle){
    try{
      var viewedList = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      viewedList = viewedList.filter(function(h){ return h !== currentProductHandle; });
      viewedList.unshift(currentProductHandle);
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(viewedList.slice(0, 12)));
    }catch(e){}
  }

  var recentlyViewedGrid = document.getElementById('recently-viewed-grid');
  if(recentlyViewedGrid && window.fetch){
    var recentlyViewedSection = recentlyViewedGrid.closest('[data-section-name]');
    var rvMoney = function(cents){ return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
    var viewedHandles = [];
    try{ viewedHandles = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]'); }catch(e){}
    viewedHandles = viewedHandles.filter(function(h){ return h !== currentProductHandle; }).slice(0, 8);
    if(viewedHandles.length === 0){
      if(recentlyViewedSection) recentlyViewedSection.style.display = 'none';
    } else {
      viewedHandles.forEach(function(handle){
        fetch('/products/' + handle + '.js')
          .then(function(r){ return r.ok ? r.json() : null; })
          .then(function(product){
            if(!product) return;
            var card = document.createElement('article');
            card.className = 'weave-card reveal in';
            var img = product.featured_image ? '<img src="' + product.featured_image + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '';
            card.innerHTML =
              '<a href="' + product.url + '" class="weave-media" style="display:block;">' + img + '</a>' +
              '<div class="weave-body">' +
                '<h3><a href="' + product.url + '" style="color:inherit;text-decoration:none;">' + product.title + '</a></h3>' +
                '<div class="price-row"><span class="price">' + rvMoney(product.price) + '</span></div>' +
              '</div>';
            recentlyViewedGrid.appendChild(card);
          })
          .catch(function(){});
      });
    }
  }

  /* ---------- Wishlist page: renders saved products from localStorage ---------- */
  var wishlistGrid = document.getElementById('wishlist-grid');
  if(wishlistGrid){
    var wishlistEmpty = document.getElementById('wishlist-empty');
    var wishlistMoney = function(cents){ return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
    var wishlistRenderGen = 0;
    var renderWishlistPage = function(){
      var myGen = ++wishlistRenderGen;
      var handles = getWishlist();
      wishlistGrid.innerHTML = '';
      if(handles.length === 0){
        if(wishlistEmpty) wishlistEmpty.style.display = 'block';
        return;
      }
      if(wishlistEmpty) wishlistEmpty.style.display = 'none';
      handles.forEach(function(handle){
        fetch('/products/' + handle + '.js')
          .then(function(r){ return r.ok ? r.json() : null; })
          .then(function(product){
            if(!product) return;
            if(myGen !== wishlistRenderGen) return;
            var firstAvailable = product.variants.find(function(v){ return v.available; });
            var card = document.createElement('article');
            card.className = 'weave-card reveal in';
            var img = product.featured_image ? '<img src="' + product.featured_image + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '';
            card.innerHTML =
              '<div class="weave-media" style="position:relative;">' +
                '<a href="' + product.url + '" style="display:block;width:100%;height:100%;">' + img + '</a>' +
                '<button type="button" class="wishlist-remove-btn" data-wishlist-toggle="' + handle + '" aria-label="Remove from wishlist" title="Remove from wishlist">&times;</button>' +
              '</div>' +
              '<div class="weave-body">' +
                '<h3><a href="' + product.url + '" style="color:inherit;text-decoration:none;">' + product.title + '</a></h3>' +
                '<div class="price-row"><span class="price">' + wishlistMoney(product.price) + '</span></div>' +
                (firstAvailable
                  ? '<button type="button" class="btn btn-sm btn-cart" style="margin-top:8px;width:100%;" data-wishlist-add-to-cart="' + firstAvailable.id + '">Add to Cart</button>'
                  : '<button type="button" class="btn btn-sm btn-ghost" style="margin-top:8px;width:100%;" disabled>Sold Out</button>') +
                '<button type="button" class="weave-link wishlist-toggle active" data-wishlist-toggle="' + handle + '" style="background:none;border:none;cursor:pointer;padding:0;margin-top:8px;">Remove from Wishlist</button>' +
              '</div>';
            wishlistGrid.appendChild(card);
          })
          .catch(function(){});
      });
    };
    renderWishlistPage();
    document.addEventListener('click', function(e){
      var removeBtn = e.target.closest('[data-wishlist-toggle]');
      if(removeBtn && wishlistGrid.contains(removeBtn)){
        wishlistRenderGen++; // invalidate any fetches already in flight for this render pass
        var card = removeBtn.closest('.weave-card');
        if(card) card.remove();
        if(wishlistGrid.children.length === 0 && wishlistEmpty) wishlistEmpty.style.display = 'block';
      }
      var addBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if(addBtn){
        var variantId = parseInt(addBtn.getAttribute('data-wishlist-add-to-cart'), 10);
        var originalLabel = addBtn.textContent;
        addBtn.disabled = true; addBtn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        })
          .then(function(res){ return res.json().then(function(data){ return { ok: res.ok, data: data }; }); })
          .then(function(result){
            if(!result.ok) throw new Error();
            addBtn.textContent = 'Added!';
            return fetch('/cart.js').then(function(r){ return r.json(); });
          })
          .then(function(cart){
            if(!cart) return;
            document.querySelectorAll('[data-cart-count-wrap]').forEach(function(wrap){
              var badge = wrap.querySelector('.cart-count');
              if(cart.item_count > 0 && !badge){ badge = document.createElement('span'); badge.className = 'cart-count'; wrap.appendChild(badge); }
              if(badge){ badge.textContent = cart.item_count; badge.style.display = cart.item_count > 0 ? '' : 'none'; }
            });
          })
          .catch(function(){ addBtn.textContent = 'Try again'; })
          .finally(function(){
            setTimeout(function(){ addBtn.disabled = false; addBtn.textContent = originalLabel; }, 1800);
          });
      }
    });
  }

  /* ---------- Collection page: mobile filter drawer ---------- */
  var filterPanel = document.getElementById('collection-filters');
  if(filterPanel){
    var filterToggle = document.getElementById('collection-filter-toggle');
    var filterClose = document.getElementById('collection-filter-close');
    if(filterToggle) filterToggle.addEventListener('click', function(){ filterPanel.classList.add('open'); });
    if(filterClose) filterClose.addEventListener('click', function(){ filterPanel.classList.remove('open'); });
  }

  /* ---------- Hero headline word cycle ---------- */
  var cycleEl = document.getElementById('cycle-word');
  if(cycleEl){
    var cycleWords = [];
    try{ cycleWords = JSON.parse(cycleEl.getAttribute('data-words') || '[]'); }catch(e){}
    if(cycleWords.length > 1){
      /* Reserve a fixed width sized to the longest cycling word BEFORE the
         interval starts. Without this, swapping to a shorter/longer word
         changes this inline-block's width, which can change how the
         heading wraps and shift the Hero's total height — cascading a
         layout shift through the ENTIRE page below it, repeating forever
         on a timer, with zero scrolling or interaction needed. This is
         exactly what was reported as constant whole-page "shaking". */
      var originalWord = cycleEl.textContent;
      var maxWidth = 0;
      cycleWords.forEach(function(word){
        cycleEl.textContent = word;
        if(cycleEl.offsetWidth > maxWidth) maxWidth = cycleEl.offsetWidth;
      });
      cycleEl.textContent = originalWord;
      cycleEl.style.minWidth = maxWidth + 'px';
      var cw = 0;
      setInterval(function(){
        cycleEl.classList.add('cycling');
        setTimeout(function(){
          cw = (cw + 1) % cycleWords.length;
          cycleEl.textContent = cycleWords[cw];
          cycleEl.classList.remove('cycling');
        }, 300);
      }, 2800);
    }
  }

  /* ---------- Sticky header shrink + announcement hide-on-scroll-down ----------
     Uses a distance threshold (not raw scroll direction) before toggling the
     announcement bar, since momentum/trackpad scrolling naturally jitters by
     a pixel or two frame-to-frame even while scrolling steadily one way —
     reacting to every pixel caused the bar (and the header bridged to its
     height) to rapidly flicker hidden/shown, which looked like the whole
     header was vibrating. requestAnimationFrame throttling avoids running
     this more than once per frame. ---------- */
  var header = document.getElementById('site-header');
  var scrollAnchorY = window.scrollY;
  var SCROLL_HIDE_THRESHOLD = 10;
  function onScroll(){
    var y = window.scrollY;
    if(header) header.classList.toggle('scrolled', y > 30);
    if(announceBar){
      /* Near the very bottom of the page, momentum/rubber-band scroll bounce
         makes scrollY oscillate by a few px even after the user has stopped —
         each oscillation crossed the threshold and re-toggled the bar (and the
         sticky header's bound `top`), which looked like shaking right by the
         footer. Freeze the show/hide state once we're at the scroll limit. */
      var atBottom = (window.innerHeight + y) >= (document.documentElement.scrollHeight - 4);
      if(atBottom){
        scrollAnchorY = y;
      }else if(y <= 60){
        if(announceBar.classList.contains('hidden')){ announceBar.classList.remove('hidden'); measureAnnounce(); }
        scrollAnchorY = y;
      }else if(y - scrollAnchorY > SCROLL_HIDE_THRESHOLD){
        if(!announceBar.classList.contains('hidden')){
          announceBar.classList.add('hidden');
          document.documentElement.style.setProperty('--announce-h', '0px');
        }
        scrollAnchorY = y;
      }else if(scrollAnchorY - y > SCROLL_HIDE_THRESHOLD){
        if(announceBar.classList.contains('hidden')){ announceBar.classList.remove('hidden'); measureAnnounce(); }
        scrollAnchorY = y;
      }
    }
  }
  var scrollTicking = false;
  window.addEventListener('scroll', function(){
    if(!scrollTicking){
      window.requestAnimationFrame(function(){ onScroll(); scrollTicking = false; });
      scrollTicking = true;
    }
  }, { passive: true });
  onScroll();

  /* ---------- Scroll reveal ---------- */
  if('IntersectionObserver' in window && !reduced){
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){ if(entry.isIntersecting){ entry.target.classList.add('in'); io.unobserve(entry.target); } });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal').forEach(function(el){ io.observe(el); });
  } else {
    document.querySelectorAll('.reveal').forEach(function(el){ el.classList.add('in'); });
  }

  /* ---------- Explorer filters (Shop by Colour & Price): clicking always
     updates a visible "here's what happens next" summary ---------- */
  var resultsTags = document.getElementById('explorer-results-tags');
  var sortSelect = document.getElementById('sort-select');
  function updateExplorerResults(){
    if(!resultsTags) return;
    var parts = [];
    var activeColor = document.querySelector('#color-row .color-dot.active');
    if(activeColor) parts.push(activeColor.getAttribute('data-name'));
    var activePrice = document.querySelector('#price-row .chip.active');
    if(activePrice) parts.push(activePrice.textContent);
    var activeFabric = document.querySelector('#fabric-row .chip.active');
    if(activeFabric) parts.push(activeFabric.textContent);
    var activePattern = document.querySelector('#pattern-row .chip.active');
    if(activePattern) parts.push(activePattern.textContent);
    if(sortSelect) parts.push('sorted by ' + sortSelect.value);
    resultsTags.textContent = parts.join(' · ');
  }
  if(resultsTags){
    ['#color-row .color-dot', '#price-row .chip', '#fabric-row .chip', '#pattern-row .chip'].forEach(function(sel){
      document.querySelectorAll(sel).forEach(function(el){
        el.addEventListener('click', function(){
          document.querySelectorAll(sel).forEach(function(c){ c.classList.remove('active'); });
          el.classList.add('active');
          updateExplorerResults();
        });
      });
    });
    if(sortSelect) sortSelect.addEventListener('change', updateExplorerResults);
    updateExplorerResults();
  }
  /* "View Matching Sarees" — real filtering via Shopify's native collection
     filters. Colour/Fabric/Pattern selections become `filter.p.tag` values
     (so they only match products actually tagged with that exact text —
     e.g. tag a product "Silk" to match the "100% Silk" fabric chip's tag
     "100% Silk"), and the price chip's min/max become the native
     `filter.v.price.gte`/`lte` range. Only runs when the button has a real
     collection to filter (data-filter-collection-url) — if the merchant set
     a custom override link instead, that plain link is used as-is. */
  var viewResultsBtn = document.getElementById('explorer-view-results');
  if(viewResultsBtn){
    var filterCollectionUrl = viewResultsBtn.getAttribute('data-filter-collection-url');
    if(filterCollectionUrl){
      viewResultsBtn.addEventListener('click', function(e){
        e.preventDefault();
        var params = [];
        var activeColor = document.querySelector('#color-row .color-dot.active');
        if(activeColor && activeColor.getAttribute('data-tag')) params.push('filter.p.tag=' + encodeURIComponent(activeColor.getAttribute('data-tag')));
        var activeFabric = document.querySelector('#fabric-row .chip.active');
        if(activeFabric && activeFabric.getAttribute('data-tag')) params.push('filter.p.tag=' + encodeURIComponent(activeFabric.getAttribute('data-tag')));
        var activePattern = document.querySelector('#pattern-row .chip.active');
        if(activePattern && activePattern.getAttribute('data-tag')) params.push('filter.p.tag=' + encodeURIComponent(activePattern.getAttribute('data-tag')));
        var activePrice = document.querySelector('#price-row .chip.active');
        if(activePrice){
          var minPrice = activePrice.getAttribute('data-min');
          var maxPrice = activePrice.getAttribute('data-max');
          if(minPrice) params.push('filter.v.price.gte=' + encodeURIComponent(minPrice));
          if(maxPrice) params.push('filter.v.price.lte=' + encodeURIComponent(maxPrice));
        }
        if(sortSelect && sortSelect.value) params.push('sort_by=' + encodeURIComponent(sortSelect.value));
        var separator = filterCollectionUrl.indexOf('?') > -1 ? '&' : '?';
        window.location.href = filterCollectionUrl + (params.length ? separator + params.join('&') : '');
      });
    }
  }

  /* ---------- Drag/swipe carousels (New Arrivals, Celebrity Video) ---------- */
  document.querySelectorAll('.carousel-track').forEach(function(track){
    var isDown = false, startX = 0, startScroll = 0, moved = false;
    track.addEventListener('pointerdown', function(e){
      isDown = true; moved = false;
      startX = e.clientX; startScroll = track.scrollLeft;
      /* Deliberately NOT calling track.setPointerCapture() here: capturing
         the pointer on every mousedown (even a plain, no-drag click) made
         some browsers redirect the resulting "click" event to this track
         element instead of letting it fire on the actual child clicked
         (e.g. a .video-card) — so clicking a video silently did nothing,
         with no console error. Drag-scrolling still works fine below via
         plain pointermove without capture. */
    });
    track.addEventListener('pointermove', function(e){
      if(!isDown) return;
      var dx = e.clientX - startX;
      if(Math.abs(dx) > 4 && !moved){
        /* Only NOW — once real movement is confirmed — do we mark this as a
           drag. Adding the "dragging" class (which sets pointer-events:none
           on children via CSS) immediately on pointerdown was the actual
           bug: it disabled clicks on every card the instant the mouse went
           down, before the browser could ever register a plain click,
           regardless of whether the mouse moved afterward at all. */
        moved = true;
        track.classList.add('dragging');
      }
      if(moved) track.scrollLeft = startScroll - dx;
    });
    function endDrag(){ isDown = false; track.classList.remove('dragging'); }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('click', function(e){ if(moved){ e.preventDefault(); e.stopPropagation(); } }, true);
  });
  document.querySelectorAll('.carousel-arrow').forEach(function(btn){
    btn.addEventListener('click', function(){
      var track = document.getElementById(btn.getAttribute('data-carousel-target'));
      if(!track) return;
      var amount = Math.min(360, track.clientWidth * 0.8);
      track.scrollBy({ left: btn.classList.contains('prev') ? -amount : amount, behavior: 'smooth' });
    });
  });

  /* ---------- Video lightbox: click any .video-card to watch it ---------- */
  var lightbox = document.getElementById('video-lightbox');
  if(lightbox){
    var lightboxPlayer = document.getElementById('video-lightbox-player');
    var lightboxTitle = document.getElementById('video-lightbox-title');
    var lightboxNotice = document.getElementById('video-lightbox-notice');
    var openVideoLightbox = function(src, title){
      if(!lightboxPlayer) return;
      if(lightboxTitle) lightboxTitle.textContent = title || '';
      if(!src){
        lightboxPlayer.removeAttribute('src');
        lightboxPlayer.style.display = 'none';
        if(lightboxNotice){
          lightboxNotice.textContent = 'No video uploaded yet — add this card’s Video URL in the section settings (Settings > Files, then paste the link here).';
          lightboxNotice.style.display = 'block';
        }
        lightbox.classList.add('open');
        lightbox.setAttribute('aria-hidden', 'false');
        return;
      }
      if(lightboxNotice) lightboxNotice.style.display = 'none';
      lightboxPlayer.style.display = '';
      lightboxPlayer.src = src;
      lightbox.classList.add('open');
      lightbox.setAttribute('aria-hidden', 'false');
      lightboxPlayer.play().catch(function(){});
    };
    var closeVideoLightbox = function(){
      if(!lightboxPlayer) return;
      lightboxPlayer.pause();
      lightboxPlayer.removeAttribute('src');
      lightboxPlayer.load();
      /* Move focus out before hiding: if the close button (or anything else
         inside the lightbox) still has keyboard focus when aria-hidden is
         set back to true, that's an invalid state for assistive tech —
         a hidden container can't contain the focused element. */
      if(document.activeElement && lightbox.contains(document.activeElement)){
        document.activeElement.blur();
      }
      lightbox.classList.remove('open');
      lightbox.setAttribute('aria-hidden', 'true');
    };
    document.querySelectorAll('.video-card').forEach(function(card){
      card.addEventListener('click', function(){
        openVideoLightbox(card.getAttribute('data-video'), card.getAttribute('data-title'));
      });
      card.addEventListener('keydown', function(e){
        if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); openVideoLightbox(card.getAttribute('data-video'), card.getAttribute('data-title')); }
      });
    });
    var lightboxCloseBtn = document.getElementById('video-lightbox-close');
    var lightboxBackdrop = document.getElementById('video-lightbox-backdrop');
    if(lightboxCloseBtn) lightboxCloseBtn.addEventListener('click', closeVideoLightbox);
    if(lightboxBackdrop) lightboxBackdrop.addEventListener('click', closeVideoLightbox);
    document.addEventListener('keydown', function(e){ if(e.key === 'Escape') closeVideoLightbox(); });
  }

  /* ---------- Testimonial rotation ---------- */
  var slides = document.querySelectorAll('.testi-slide');
  if(slides.length){
    var dots = document.querySelectorAll('#testi-dots button');
    var ti = 0;
    function showTesti(i){
      slides.forEach(function(s,idx){ s.classList.toggle('active', idx===i); });
      dots.forEach(function(d,idx){ d.classList.toggle('active', idx===i); });
      ti = i;
    }
    dots.forEach(function(d,idx){ d.addEventListener('click', function(){ showTesti(idx); }); });
    setInterval(function(){ showTesti((ti+1) % slides.length); }, 5500);
  }
})();
